import type { AgendaItem, Commitment, HomeMetrics, NextMeeting } from '../../api/client'
import { meetingDay, parseDay } from '../../dates'

/*
  What Home opens with.

  It used to open with the profile nudge, which meant the first words a signed-in owner
  read were "Add your annual revenue" — a request, on every one of the nine tabs, before
  the product had said anything about their business at all. Home had no heading of its
  own; it went straight from the nudge into a grid of cards.

  So: the company, the day, and the one sentence that is true about this week. The sentence
  is built from what is already loaded for the grid below, in the order somebody actually
  cares about it — what is late, then when the board next sits, then what is waiting.

  The company name is not the heading any more. It was set at 28px and was the largest
  thing on a phone screen, which spent the top eighty pixels of the product telling an
  owner what his own company is called. It sits with the date on one muted line instead,
  and the figures and the late count take the room back.
*/

/** The screens the standing line can send somebody to. Home's own navigation union. */
type Destination = 'table' | 'commitments' | 'meetings' | 'board' | 'company' | 'numbers'

export function Masthead({
  company, next, inbox, commitments, metrics, onNavigate,
}: {
  company: string | null
  next: NextMeeting | null
  inbox: AgendaItem[]
  commitments: Commitment[]
  /** The same read the anomaly card uses, fetched once by Home and shared. */
  metrics: HomeMetrics | null
  onNavigate: (screen: Destination) => void
}) {
  // dueDate is a calendar day, so it is parsed as one. new Date("2026-09-04") is midnight
  // UTC and reads a day early west of Greenwich, which is what src/dates.ts exists for.
  const midnight = new Date()
  midnight.setHours(0, 0, 0, 0)
  const overdue = commitments.filter(
    c => c.status === 'open' && c.dueDate && parseDay(c.dueDate) < midnight,
  )

  const line = standing(next, inbox, overdue.length)

  /*
    Who will actually raise this on Friday, and when.

    The count and the sentence are facts about the owner. The follow-up line is the half
    that makes this a board rather than a badge: SPEC §10 is that nobody else in this man's
    life asks whether he did the thing he said he would do, and a card that shows a face
    without saying what the face is going to do with it is decoration.
  */
  const raiser = line.late ? raiserFor(overdue) : null

  return (
    <header className="masthead">
      {/*
        One muted line, above the figures. The company and the day are context for what
        follows rather than a heading over it, so they sit together at reading size with a
        middot between them.
      */}
      <p className="masthead__who">
        <span className="masthead__name">{company ?? 'Your board'}</span>
        {/*
          The middot is its own element so it can be taken away. On a phone the name and the
          day do not fit on one line together — "Halvorsen Plumbing & Drain · Tuesday, 8
          September" is about 375px of a 358px column — and a wrapped flex row left the dot
          stranded at the end of the first line with nothing after it.
        */}
        <span className="masthead__sep" aria-hidden="true">·</span>
        <span className="masthead__date">
          {new Date().toLocaleDateString(undefined, {
            weekday: 'long', day: 'numeric', month: 'long',
          })}
        </span>
      </p>

      <MetricStrip metrics={metrics} onNavigate={onNavigate} />

      {/*
        The sentence names a place, so it goes there.

        "2 things you committed to are past their date" was the first thing an owner read on
        the first screen, set in the largest body type on the page, and it was inert: the
        only way to act on it was to work out for yourself that past-due commitments live
        behind Actions. Every branch that names something is a button now. The one that does
        not is the empty state, where the thing to do is the Ask card below.
      */}
      {line.to !== undefined ? (
        <button
          type="button"
          className={`standing${line.late ? ' standing--late' : ''}`}
          onClick={() => onNavigate(line.to as Destination)}
        >
          <span className="standing__body">
            {line.lead && (
              <span className="standing__count">
                <span className="standing__lead">{line.lead}</span>
                {line.label && <span className="standing__label">{line.label}</span>}
              </span>
            )}

            <span className={`standing__text${line.lead ? ' standing__text--sub' : ''}`}>
              {line.text}
            </span>

            {raiser && (
              <span className="standing__who">
                {raiser.face && (
                  <img className="standing__face" src={raiser.face} alt="" loading="lazy" />
                )}
                <span className="standing__whotext">
                  {raiser.name}&rsquo;s putting {overdue.length === 1 ? 'this' : 'these'}{' '}
                  on the table {meetingDay(raiser.on)}.
                </span>
              </span>
            )}
          </span>

          <span className="standing__go" aria-hidden="true">&rsaquo;</span>
        </button>
      ) : (
        <p className="masthead__line">{line.text}</p>
      )}
    </header>
  )
}

/**
 * Who raises a whole group of late commitments, and when.
 *
 * Every commitment already carries the seat the follow-up job picked for it, chosen
 * server-side from competence in that commitment's own domain, so the name on the card is
 * the name that turns up. This card speaks for the group rather than for one of them, so
 * it takes the seat holding the most of the group and the soonest date any of those goes
 * up. A tie goes to whoever raises theirs first, which keeps the line stable across
 * renders instead of picking whichever the sort happened to leave first.
 *
 * Null when no follow-up has been picked yet, and then nothing is drawn rather than a guess.
 */
function raiserFor(overdue: Commitment[]): { name: string; face: string | null; on: string } | null {
  const named = overdue.filter(c => c.followUpBy && c.followUpOn)
  if (named.length === 0) return null

  const byName = new Map<string, Commitment[]>()
  for (const c of named) {
    const key = c.followUpBy as string
    byName.set(key, [...(byName.get(key) ?? []), c])
  }

  const soonest = (g: Commitment[]) =>
    g.reduce((a, b) => ((a.followUpOn as string) <= (b.followUpOn as string) ? a : b))

  let best: Commitment[] | null = null
  for (const group of byName.values()) {
    if (best === null
      || group.length > best.length
      || (group.length === best.length
        && (soonest(group).followUpOn as string) < (soonest(best).followUpOn as string))) {
      best = group
    }
  }

  if (best === null) return null
  const first = soonest(best)
  const name = first.followUpBy as string

  return {
    name: name.split(/\s+/)[0] ?? name,
    face: first.followUpFace,
    on: first.followUpOn as string,
  }
}

/*
  The figures this trade steers by, on the line under the company's own name.

  They were tiles in a card further down the page, which is where a number goes to be
  looked up rather than read. Up here they are the second thing on the screen, which is
  what an owner opening this at 6am is actually after. Same read, same figures, one place:
  the card below keeps the anomaly and nothing else, so no number appears twice.

  Three across a phone does not fit. It used to be a nowrap row that scrolled, which showed
  the third label sheared off at the right edge with nothing to say it moved, so it read as
  a rendering fault rather than an invitation. Two figures an owner can read beat three he
  cannot: below 560px the third is dropped and the two that remain take half the width
  each, with the hairline still between them.

  Direction is not the same as good, so the arrow takes its colour from the metric's own
  higherIsBetter rather than from the sign. Callbacks rising is bad.
*/
function MetricStrip({
  metrics, onNavigate,
}: {
  metrics: HomeMetrics | null
  onNavigate: (screen: Destination) => void
}) {
  const tiles = (metrics?.tiles ?? []).slice(0, 3)
  if (tiles.length === 0) return null

  return (
    <button type="button" className="kpis" onClick={() => onNavigate('numbers')}>
      {tiles.map(t => {
        const rising = (t.delta ?? 0) > 0
        const flat = t.delta === null || t.delta === 0
        const good = rising === t.higherIsBetter
        return (
          <span className="kpi" key={t.key}>
            <span className="kpi__label">{t.label}</span>
            <span className="kpi__figure">
              <span className="kpi__value">{money(t.value, t.unit)}</span>
              {/*
                A figure with no direction on it is something to look up rather than
                something to look at, and which way it is moving is the reason to look.
                Flat and unknown both stay silent: an arrow on a 0% is a claim this does
                not know enough to make.
              */}
              {/*
                A triangle rather than an arrow. ↑ falls back to a hairline stroke in the
                product's own face, and "$752k ↑6%" rendered as "$752k 16%" — the one
                reading of a figure that is worse than no figure at all.
              */}
              {!flat && (
                <span
                  className={`kpi__delta ${good ? 'is-good' : 'is-bad'}`}
                  aria-label={`${rising ? 'up' : 'down'} ${Math.abs(t.deltaPercent ?? 0).toFixed(0)} percent`}
                >
                  <span className="kpi__arrow" aria-hidden="true">{rising ? '▲' : '▼'}</span>
                  {Math.abs(t.deltaPercent ?? 0).toFixed(0)}%
                </span>
              )}
            </span>
          </span>
        )
      })}
    </button>
  )
}

/** Compact enough to sit two-across on a phone: $752, 68%, 1.2k. */
function money(n: number, unit: string): string {
  if (unit === 'percent') return `${Math.round(n)}%`
  // The API says "currency" (HomeRepository.UnitFor); "usd" is the stored unit and is
  // accepted too. Checking only "usd" printed every dollar tile as a bare count, "1243k".
  if (unit === 'currency' || unit === 'usd') {
    const a = Math.abs(n)
    // The sign has to survive: a net profit of -40,000 printed as "$40k" reads as a good month.
    const sign = n < 0 ? '-' : ''
    if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(1)}m`
    if (a >= 10_000) return `${sign}$${Math.round(a / 1000)}k`
    return `${sign}$${Math.round(a).toLocaleString('en-US')}`
  }
  return n >= 10_000 ? `${Math.round(n / 1000)}k` : String(Math.round(n))
}

/**
 * The line under the company name, and where it points.
 *
 * Worst news first: a commitment past its date is the one thing here somebody has already
 * failed at, and it outranks a meeting three days out.
 */
function standing(
  next: NextMeeting | null, inbox: AgendaItem[], overdue: number,
): { lead?: string; label?: string; text: string; to?: Destination; late?: boolean } {
  const midnight = new Date()
  midnight.setHours(0, 0, 0, 0)

  if (overdue > 0) {
    /*
      A count, a sentence and a follow-up line, rather than one sentence.

      It used to read "5 things you committed to are past their date." on a single line,
      which made the number part of a paragraph instead of the thing on the card. The count
      leads on its own line because it is what the owner is answering to; the sentence
      under it says what the number counts; the line under that says who raises it.
    */
    return {
      lead: String(overdue),
      label: 'past due',
      text: overdue === 1 ? 'thing you committed to' : 'things you committed to',
      to: 'commitments',
      late: true,
    }
  }

  const waiting = inbox.length

  // scheduledFor is an instant rather than a calendar day, so it is read as one.
  if (next?.scheduledFor) {
    const day = new Date(next.scheduledFor)
    day.setHours(0, 0, 0, 0)
    const days = Math.round((day.getTime() - midnight.getTime()) / 86_400_000)

    /*
      The day, not a count of days. The meeting card three inches to the right counts from
      the meeting's own hour, so a 5am Friday meeting is "in 2 days" there on a Tuesday
      afternoon while whole calendar days make it three. Two numbers disagreeing on one
      screen is worse than either of them, and naming the day cannot disagree with anything.
    */
    const when = days <= 0 ? 'today'
      : days === 1 ? 'tomorrow'
      : days < 7 ? `on ${day.toLocaleDateString(undefined, { weekday: 'long' })}`
      : `on ${day.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })}`

    // The table either way. With items on it, that is where they are; with nothing on it,
    // the sentence is an invitation to put something there.
    return waiting === 0
      ? {
          text: `The board meets ${when}. Nothing on the table yet, so it will set its own agenda.`,
          to: 'table',
        }
      : {
          text: `The board meets ${when}, with ${waiting} ${waiting === 1 ? 'item' : 'items'} on the table.`,
          to: 'table',
        }
  }

  // Nothing scheduled and nothing waiting. What to do about it is the Ask card immediately
  // below, not another screen, so this is the one line that stays a sentence.
  return waiting === 0
    ? { text: 'Nothing on the table. Ask the board anything, or put something up for the next meeting.' }
    : {
        text: `${waiting} ${waiting === 1 ? 'item' : 'items'} waiting for the next meeting.`,
        to: 'table',
      }
}
